package com.partejar.app.feature.home.presentation

import androidx.compose.foundation.background
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.WindowInsetsSides
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.only
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.GridItemSpan
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items as gridItems
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.partejar.app.core.ui.components.PartejarAlert
import com.partejar.app.core.ui.components.PartejarAlertTone
import com.partejar.app.core.ui.components.PartejarButton
import com.partejar.app.core.ui.components.PartejarCard
import com.partejar.app.core.ui.components.PartejarDockDestination
import com.partejar.app.core.ui.components.PartejarDockScaffold
import com.partejar.app.core.ui.components.PartejarDraggableFab
import com.partejar.app.core.ui.components.PartejarLayout
import com.partejar.app.core.ui.components.PartejarWindowSize
import com.partejar.app.core.ui.components.partejarWindowSize
import com.partejar.app.core.ui.components.PartejarThemeToggleAction
import com.partejar.app.core.ui.components.PartejarSkeleton
import com.partejar.app.core.ui.components.PartejarSkeletonText
import com.partejar.app.core.ui.domain.gestante.GestanteSummary
import com.partejar.app.core.ui.domain.gestante.GestanteSummaryCard
import com.partejar.app.core.ui.domain.gestante.GestanteSummaryStatus
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarShapes
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.feature.home.presentation.components.HomeTimeline
import com.partejar.app.feature.home.data.HomeAttentionLevel
import com.partejar.app.feature.home.data.HomePendingType
import org.koin.compose.koinInject

@Composable
fun HomeScreen(
    isDarkTheme: Boolean,
    onThemeToggle: () -> Unit,
    onDestinationSelected: (PartejarDockDestination) -> Unit,
    onAttentionSelected: (() -> Unit)? = null,
    onGestanteSelected: ((String) -> Unit)? = null,
    onNewRelationship: (() -> Unit)? = null,
    modifier: Modifier = Modifier,
    viewModel: HomeViewModel = koinInject()
) {
    val uiState by viewModel.uiState.collectAsState()

    HomeContent(
        state = uiState,
        isDarkTheme = isDarkTheme,
        onThemeToggle = onThemeToggle,
        onSeeAllGestantes = {
            // Utiliza a lambda apenas para solicitar navegação externa, sem impactar a UI local
            onDestinationSelected(PartejarDockDestination.GESTANTES)
        },
        onAttentionSelected = onAttentionSelected,
        onGestanteSelected = onGestanteSelected,
        onNewRelationship = onNewRelationship,
        onRetry = viewModel::retry,
        modifier = modifier
    )
}

@Composable
fun HomeContent(
    state: HomeUiState,
    isDarkTheme: Boolean,
    onThemeToggle: () -> Unit,
    onSeeAllGestantes: () -> Unit,
    onAttentionSelected: (() -> Unit)?,
    onGestanteSelected: ((String) -> Unit)?,
    onRetry: () -> Unit,
    modifier: Modifier = Modifier,
    onNewRelationship: (() -> Unit)? = null
) {
    PartejarDockScaffold(modifier = modifier.fillMaxSize()) { scaffoldPadding, dockClearance ->
        Box(Modifier.fillMaxSize()) {
            when (partejarWindowSize) {
                PartejarWindowSize.COMPACT -> HomeCompactContent(
                    state = state,
                    isDarkTheme = isDarkTheme,
                    onThemeToggle = onThemeToggle,
                    onSeeAllGestantes = onSeeAllGestantes,
                    onAttentionSelected = onAttentionSelected,
                    onGestanteSelected = onGestanteSelected,
                    onRetry = onRetry,
                    scaffoldPadding = scaffoldPadding,
                    dockClearance = dockClearance
                )
                PartejarWindowSize.MEDIUM -> HomeMediumContent(
                    state = state,
                    isDarkTheme = isDarkTheme,
                    onThemeToggle = onThemeToggle,
                    onSeeAllGestantes = onSeeAllGestantes,
                    onAttentionSelected = onAttentionSelected,
                    onGestanteSelected = onGestanteSelected,
                    onRetry = onRetry,
                    scaffoldPadding = scaffoldPadding,
                    dockClearance = dockClearance
                )
                PartejarWindowSize.EXPANDED -> HomeExpandedContent(
                    state = state,
                    isDarkTheme = isDarkTheme,
                    onThemeToggle = onThemeToggle,
                    onSeeAllGestantes = onSeeAllGestantes,
                    onAttentionSelected = onAttentionSelected,
                    onGestanteSelected = onGestanteSelected,
                    onRetry = onRetry,
                    scaffoldPadding = scaffoldPadding,
                    dockClearance = dockClearance
                )
            }
            onNewRelationship?.let { onNew ->
                PartejarDraggableFab(
                    onClick = onNew,
                    dockClearance = dockClearance,
                    contentDescription = "Novo contato"
                )
            }
        }
    }
}

@Composable
private fun HomeCompactContent(
    state: HomeUiState,
    isDarkTheme: Boolean,
    onThemeToggle: () -> Unit,
    onSeeAllGestantes: () -> Unit,
    onAttentionSelected: (() -> Unit)?,
    onGestanteSelected: ((String) -> Unit)?,
    onRetry: () -> Unit,
    scaffoldPadding: PaddingValues,
    dockClearance: Dp
) {
    HomeSingleColumnContent(
        state = state,
        isDarkTheme = isDarkTheme,
        onThemeToggle = onThemeToggle,
        onSeeAllGestantes = onSeeAllGestantes,
        onAttentionSelected = onAttentionSelected,
        onGestanteSelected = onGestanteSelected,
        onRetry = onRetry,
        scaffoldPadding = scaffoldPadding,
        dockClearance = dockClearance,
        horizontalGutter = PartejarTheme.spacing.space16
    )
}

@Composable
private fun HomeMediumContent(
    state: HomeUiState,
    isDarkTheme: Boolean,
    onThemeToggle: () -> Unit,
    onSeeAllGestantes: () -> Unit,
    onAttentionSelected: (() -> Unit)?,
    onGestanteSelected: ((String) -> Unit)?,
    onRetry: () -> Unit,
    scaffoldPadding: PaddingValues,
    dockClearance: Dp
) {
    HomeSingleColumnContent(
        state = state,
        isDarkTheme = isDarkTheme,
        onThemeToggle = onThemeToggle,
        onSeeAllGestantes = onSeeAllGestantes,
        onAttentionSelected = onAttentionSelected,
        onGestanteSelected = onGestanteSelected,
        onRetry = onRetry,
        scaffoldPadding = scaffoldPadding,
        dockClearance = dockClearance,
        horizontalGutter = PartejarTheme.spacing.space24
    )
}

@Composable
private fun HomeSingleColumnContent(
    state: HomeUiState,
    isDarkTheme: Boolean,
    onThemeToggle: () -> Unit,
    onSeeAllGestantes: () -> Unit,
    onAttentionSelected: (() -> Unit)?,
    onGestanteSelected: ((String) -> Unit)?,
    onRetry: () -> Unit,
    scaffoldPadding: PaddingValues,
    dockClearance: Dp,
    horizontalGutter: Dp
) {
    val contentState = state as? HomeUiState.Content
    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(scaffoldPadding)
    ) {
        HomeEditorialHeader(
            greeting = contentState?.greeting,
            dateLabel = contentState?.dateLabel,
            briefing = contentState?.dailyBriefing(),
            isDarkTheme = isDarkTheme,
            onThemeToggle = onThemeToggle,
            horizontalGutter = horizontalGutter
        )
        when (state) {
            HomeUiState.Loading -> LoadingHome(dockClearance, horizontalGutter)
            HomeUiState.Empty -> EmptyHome(dockClearance)
            is HomeUiState.Error -> ErrorHome(
                message = state.message,
                dockClearance = dockClearance,
                horizontalGutter = horizontalGutter,
                onRetry = onRetry
            )
            is HomeUiState.Content -> ContentHome(
                state = state,
                dockClearance = dockClearance,
                horizontalGutter = horizontalGutter,
                onSeeAllGestantes = onSeeAllGestantes,
                onAttentionSelected = onAttentionSelected,
                onGestanteSelected = onGestanteSelected
            )
        }
    }
}

@Composable
private fun HomeExpandedContent(
    state: HomeUiState,
    isDarkTheme: Boolean,
    onThemeToggle: () -> Unit,
    onSeeAllGestantes: () -> Unit,
    onAttentionSelected: (() -> Unit)?,
    onGestanteSelected: ((String) -> Unit)?,
    onRetry: () -> Unit,
    scaffoldPadding: PaddingValues,
    dockClearance: Dp
) {
    val contentState = state as? HomeUiState.Content
    Box(
        modifier = Modifier
            .fillMaxSize()
            .padding(scaffoldPadding),
        contentAlignment = Alignment.TopCenter
    ) {
        Column(
            modifier = Modifier
                .widthIn(max = PartejarLayout.contentMaxWidth)
                .fillMaxWidth()
                .fillMaxSize()
        ) {
            HomeDesktopHeader(
                greeting = contentState?.greeting,
                dateLabel = contentState?.dateLabel,
                isDarkTheme = isDarkTheme,
                onThemeToggle = onThemeToggle
            )
            when (state) {
                HomeUiState.Loading -> ExpandedLoadingHome(dockClearance)
                HomeUiState.Empty -> ExpandedEmptyHome(dockClearance)
                is HomeUiState.Error -> ExpandedErrorHome(
                    message = state.message,
                    dockClearance = dockClearance,
                    onRetry = onRetry
                )
                is HomeUiState.Content -> ExpandedContentHome(
                    state = state,
                    dockClearance = dockClearance,
                    onSeeAllGestantes = onSeeAllGestantes,
                    onAttentionSelected = onAttentionSelected,
                    onGestanteSelected = onGestanteSelected
                )
            }
        }
    }
}

@Composable
private fun HomeEditorialHeader(
    greeting: HomeGreeting?,
    dateLabel: String?,
    briefing: String?,
    isDarkTheme: Boolean,
    onThemeToggle: () -> Unit,
    horizontalGutter: Dp
) {
    val spacing = PartejarTheme.spacing
    val atmosphere = Brush.verticalGradient(
        colors = listOf(
            MaterialTheme.colorScheme.tertiary.copy(alpha = 0.10f),
            MaterialTheme.colorScheme.primary.copy(alpha = 0.045f),
            Color.Transparent
        )
    )

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .background(atmosphere)
            .windowInsetsPadding(
                WindowInsets.safeDrawing.only(
                    WindowInsetsSides.Top + WindowInsetsSides.Horizontal
                )
            )
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(
                    start = horizontalGutter,
                    end = horizontalGutter,
                    top = spacing.space16,
                    bottom = spacing.space20
                ),
            horizontalArrangement = Arrangement.spacedBy(spacing.space12),
            verticalAlignment = Alignment.Top
        ) {
            GreetingIcon(period = greeting?.period)
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(spacing.space4)
            ) {
                Text(
                    text = greeting?.message ?: "Partejar",
                    modifier = Modifier.semantics { heading() },
                    style = MaterialTheme.typography.headlineMedium
                )
                dateLabel?.let {
                    Text(
                        text = it,
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
                briefing?.let {
                    Text(
                        text = it,
                        style = MaterialTheme.typography.labelLarge,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }
            Surface(
                shape = PartejarShapes.pill,
                color = MaterialTheme.colorScheme.surfaceContainerLow.copy(alpha = 0.88f)
            ) {
                PartejarThemeToggleAction(
                    isDarkTheme = isDarkTheme,
                    onClick = onThemeToggle
                )
            }
        }
    }
}

@Composable
private fun HomeDesktopHeader(
    greeting: HomeGreeting?,
    dateLabel: String?,
    isDarkTheme: Boolean,
    onThemeToggle: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    val atmosphere = Brush.verticalGradient(
        colors = listOf(
            MaterialTheme.colorScheme.tertiary.copy(alpha = 0.10f),
            MaterialTheme.colorScheme.primary.copy(alpha = 0.045f),
            Color.Transparent
        )
    )

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .background(atmosphere)
            .windowInsetsPadding(
                WindowInsets.safeDrawing.only(
                WindowInsetsSides.Top + WindowInsetsSides.Horizontal
            )
        )
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(
                    start = spacing.space32,
                    end = spacing.space32,
                    top = spacing.space20,
                    bottom = spacing.space20
                ),
            horizontalArrangement = Arrangement.spacedBy(spacing.space20),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(spacing.space4)
            ) {
                Text(
                    text = "Partejar",
                    style = MaterialTheme.typography.labelLarge,
                    color = MaterialTheme.colorScheme.primary
                )
                greeting?.let { currentGreeting ->
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(spacing.space12),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        GreetingIcon(period = currentGreeting.period)
                        Column(verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
                            Text(
                                text = currentGreeting.message,
                                modifier = Modifier.semantics { heading() },
                                style = MaterialTheme.typography.headlineLarge
                            )
                            dateLabel?.let { date ->
                                Text(
                                    text = date,
                                    style = MaterialTheme.typography.titleSmall,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            }
                        }
                    }
                }
            }
            Surface(
                shape = PartejarShapes.pill,
                color = MaterialTheme.colorScheme.surfaceContainerLow.copy(alpha = 0.88f)
            ) {
                PartejarThemeToggleAction(
                    isDarkTheme = isDarkTheme,
                    onClick = onThemeToggle
                )
            }
        }
    }
}

@Composable
private fun GreetingIcon(period: HomeGreetingPeriod?) {
    val dimensions = PartejarTheme.dimensions
    Icon(
        imageVector = when (period) {
            HomeGreetingPeriod.MORNING -> PartejarIcons.Greeting.morning
            HomeGreetingPeriod.AFTERNOON -> PartejarIcons.Greeting.afternoon
            HomeGreetingPeriod.NIGHT -> PartejarIcons.Greeting.night
            null -> PartejarIcons.Navigation.home
        },
        contentDescription = null,
        modifier = Modifier.size(dimensions.iconDefault),
        tint = when (period) {
            HomeGreetingPeriod.MORNING,
            HomeGreetingPeriod.AFTERNOON -> MaterialTheme.colorScheme.tertiary
            HomeGreetingPeriod.NIGHT,
            null -> MaterialTheme.colorScheme.primary
        }
    )
}

@Composable
private fun ColumnScope.ContentHome(
    state: HomeUiState.Content,
    dockClearance: Dp,
    horizontalGutter: Dp,
    onSeeAllGestantes: () -> Unit,
    onAttentionSelected: (() -> Unit)?,
    onGestanteSelected: ((String) -> Unit)?
) {
    val spacing = PartejarTheme.spacing

    LazyColumn(
        modifier = Modifier
        .fillMaxWidth()
        .weight(1f),
        contentPadding = PaddingValues(
            start = horizontalGutter,
            end = horizontalGutter,
            top = spacing.space4,
            bottom = dockClearance
        ),
        verticalArrangement = Arrangement.spacedBy(spacing.space16)
    ) {
        if (state.attentionItems.isNotEmpty()) {
            item(key = "attention") {
                AttentionSection(
                    items = state.attentionItems,
                    onClick = if (state.attentionItems.any { it.isCommercialAttention() }) {
                        onAttentionSelected
                    } else {
                        null
                    }
                )
            }
        }

        item(key = "upcoming-header") {
            SectionHeader(
                icon = PartejarIcons.Navigation.gestantes,
                title = "Próximas gestantes",
                action = {
                    TextButton(
                        onClick = onSeeAllGestantes,
                        modifier = Modifier.heightIn(
                            min = PartejarTheme.dimensions.minimumTouchTarget
                        )
                    ) {
                        Text(
                            text = "Ver todas",
                            style = MaterialTheme.typography.labelLarge,
                            color = MaterialTheme.colorScheme.primary
                        )
                    }
                }
            )
        }

        items(
            items = state.upcomingPregnancies,
            key = { it.id }
        ) { pregnancy ->
            GestanteSummaryCard(
                summary = pregnancy.toSummary(),
                onClick = onGestanteSelected?.let { selected -> { selected(pregnancy.id) } },
                onActionClick = pregnancy.actionRequired?.let {
                    onGestanteSelected?.let { selected -> { selected(pregnancy.id) } }
                }
            )
        }

        if (state.todayAppointments.isNotEmpty()) {
            item(key = "today-spacing") {
                Box(modifier = Modifier.size(spacing.space8))
            }
            item(key = "today-header") {
                SectionHeader(
                    icon = PartejarIcons.Clinical.schedule,
                    title = "Hoje",
                    subtitle = if (state.todayAppointments.size == 1) {
                        "1 compromisso programado"
                    } else {
                        "${state.todayAppointments.size} compromissos programados"
                    }
                )
            }
            item(key = "today-timeline") {
                HomeTimeline(appointments = state.todayAppointments)
            }
        }
    }
}

@Composable
private fun ColumnScope.ExpandedContentHome(
    state: HomeUiState.Content,
    dockClearance: Dp,
    onSeeAllGestantes: () -> Unit,
    onAttentionSelected: (() -> Unit)?,
    onGestanteSelected: ((String) -> Unit)?
) {
    val spacing = PartejarTheme.spacing
    val upcomingCardMaxWidth = PartejarLayout.contentMaxWidth / 3
    LazyVerticalGrid(
        columns = GridCells.Adaptive(minSize = 320.dp),
        modifier = Modifier
            .fillMaxWidth()
            .weight(1f),
        contentPadding = PaddingValues(
            start = spacing.space32,
            end = spacing.space32,
            top = spacing.space16,
            bottom = dockClearance
        ),
        horizontalArrangement = Arrangement.spacedBy(spacing.space24),
        verticalArrangement = Arrangement.spacedBy(spacing.space20)
    ) {
        item(span = { GridItemSpan(maxLineSpan) }) {
            HomeDesktopOverview(
                state = state,
                onAttentionSelected = onAttentionSelected,
                modifier = Modifier.fillMaxWidth()
            )
        }
        item(span = { GridItemSpan(maxLineSpan) }) {
            SectionHeader(
                icon = PartejarIcons.Navigation.gestantes,
                title = "Próximas gestantes",
                action = {
                    TextButton(
                        onClick = onSeeAllGestantes,
                        modifier = Modifier.heightIn(
                            min = PartejarTheme.dimensions.minimumTouchTarget
                        )
                    ) {
                        Text("Ver todas")
                    }
                }
            )
        }
        if (state.upcomingPregnancies.isEmpty()) {
            item(span = { GridItemSpan(maxLineSpan) }) {
                HomeDesktopNotice(
                    icon = PartejarIcons.Navigation.gestantes,
                    title = "Nenhuma gestante próxima",
                    description = "Não há atendimentos próximos para mostrar.",
                    tint = MaterialTheme.colorScheme.primary
                )
            }
        } else {
            gridItems(
                items = state.upcomingPregnancies,
                key = { it.id }
            ) { pregnancy ->
                GestanteSummaryCard(
                    summary = pregnancy.toSummary(),
                    modifier = Modifier.widthIn(max = upcomingCardMaxWidth),
                    onClick = onGestanteSelected?.let { selected -> { selected(pregnancy.id) } },
                    onActionClick = pregnancy.actionRequired?.let {
                        onGestanteSelected?.let { selected -> { selected(pregnancy.id) } }
                    }
                )
            }
        }
    }
}

@Composable
private fun HomeDesktopOverview(
    state: HomeUiState.Content,
    onAttentionSelected: (() -> Unit)?,
    modifier: Modifier = Modifier
) {
    val spacing = PartejarTheme.spacing
    Row(
        modifier = modifier,
        horizontalArrangement = Arrangement.spacedBy(spacing.space24),
        verticalAlignment = Alignment.Top
    ) {
        Column(
            modifier = Modifier.weight(0.95f),
            verticalArrangement = Arrangement.spacedBy(spacing.space12)
        ) {
            SectionHeader(
                icon = PartejarIcons.Feedback.attention,
                title = "Pontos de atenção"
            )
            if (state.attentionItems.isEmpty()) {
                HomeDesktopNotice(
                    icon = PartejarIcons.Feedback.success,
                    title = "Tudo em dia por aqui",
                    description = "Sem pontos de atenção no momento.",
                    tint = PartejarTheme.colors.success
                )
            } else {
                AttentionSection(
                    items = state.attentionItems,
                    onClick = if (state.attentionItems.any { it.isCommercialAttention() }) {
                        onAttentionSelected
                    } else {
                        null
                    }
                )
            }
        }
        Column(
            modifier = Modifier.weight(1.05f),
            verticalArrangement = Arrangement.spacedBy(spacing.space12)
        ) {
            SectionHeader(
                icon = PartejarIcons.Clinical.schedule,
                title = "Agenda de hoje",
                subtitle = when (state.todayAppointments.size) {
                    0 -> null
                    1 -> "1 compromisso programado"
                    else -> "${state.todayAppointments.size} compromissos programados"
                }
            )
            if (state.todayAppointments.isEmpty()) {
                HomeDesktopNotice(
                    icon = PartejarIcons.Clinical.schedule,
                    title = "Agenda livre",
                    description = "Não há compromissos para mostrar hoje.",
                    tint = MaterialTheme.colorScheme.primary
                )
            } else {
                HomeTimeline(appointments = state.todayAppointments)
            }
        }
    }
}

@Composable
private fun HomeDesktopNotice(
    icon: ImageVector,
    title: String,
    description: String,
    tint: Color,
    modifier: Modifier = Modifier
) {
    val spacing = PartejarTheme.spacing
    PartejarCard(modifier = modifier.fillMaxWidth()) {
        Row(
            horizontalArrangement = Arrangement.spacedBy(spacing.space12),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(
                imageVector = icon,
                contentDescription = null,
                modifier = Modifier.size(PartejarTheme.dimensions.iconDefault),
                tint = tint
            )
            Column(verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
                Text(text = title, style = MaterialTheme.typography.titleSmall)
                Text(
                    text = description,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
    }
}

@Composable
private fun ColumnScope.ExpandedLoadingHome(dockClearance: Dp) {
    val spacing = PartejarTheme.spacing
    LazyVerticalGrid(
        columns = GridCells.Adaptive(minSize = 320.dp),
        modifier = Modifier
            .fillMaxWidth()
            .weight(1f),
        contentPadding = PaddingValues(
            start = spacing.space32,
            end = spacing.space32,
            top = spacing.space16,
            bottom = dockClearance
        ),
        horizontalArrangement = Arrangement.spacedBy(spacing.space24),
        verticalArrangement = Arrangement.spacedBy(spacing.space20)
    ) {
        item(span = { GridItemSpan(maxLineSpan) }) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(spacing.space24)
            ) {
                repeat(2) {
                    PartejarCard(
                        modifier = Modifier
                            .weight(1f)
                            .heightIn(min = 188.dp)
                    ) {
                        Column(verticalArrangement = Arrangement.spacedBy(spacing.space16)) {
                            PartejarSkeletonText(
                                Modifier.fillMaxWidth(0.42f).height(spacing.space20)
                            )
                            repeat(2) {
                                Row(
                                    horizontalArrangement = Arrangement.spacedBy(spacing.space12),
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    PartejarSkeleton(
                                        Modifier.size(PartejarTheme.dimensions.minimumTouchTarget)
                                    )
                                    Column(
                                        modifier = Modifier.weight(1f),
                                        verticalArrangement = Arrangement.spacedBy(spacing.space8)
                                    ) {
                                        PartejarSkeletonText(
                                            Modifier.fillMaxWidth(0.76f).height(spacing.space16)
                                        )
                                        PartejarSkeletonText(
                                            Modifier.fillMaxWidth(0.52f).height(spacing.space12)
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        item(span = { GridItemSpan(maxLineSpan) }) {
            PartejarSkeletonText(
                Modifier.fillMaxWidth(0.3f).height(spacing.space24)
            )
        }
        items(count = 3) {
            PartejarCard(
                modifier = Modifier
                    .fillMaxWidth()
                    .heightIn(min = 176.dp)
            ) {
                Column(verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
                    PartejarSkeletonText(
                        Modifier.fillMaxWidth(0.58f).height(spacing.space20)
                    )
                    PartejarSkeletonText(
                        Modifier.fillMaxWidth(0.76f).height(spacing.space12)
                    )
                    PartejarSkeletonText(
                        Modifier.fillMaxWidth(0.48f).height(spacing.space12)
                    )
                    PartejarSkeletonText(
                        Modifier.fillMaxWidth(0.64f).height(spacing.space12)
                    )
                }
            }
        }
    }
}

@Composable
private fun ColumnScope.ExpandedEmptyHome(dockClearance: Dp) {
    val spacing = PartejarTheme.spacing
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .weight(1f)
            .verticalScroll(rememberScrollState())
            .padding(
                start = spacing.space32,
                end = spacing.space32,
                top = spacing.space24,
                bottom = dockClearance
            )
    ) {
        PartejarCard(
            modifier = Modifier
                .widthIn(max = 960.dp)
                .fillMaxWidth()
                .align(Alignment.CenterHorizontally)
                .heightIn(min = 224.dp),
            contentPadding = PaddingValues(spacing.space24)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(spacing.space32),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(
                    modifier = Modifier.weight(1.15f),
                    horizontalArrangement = Arrangement.spacedBy(spacing.space16),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Surface(
                        modifier = Modifier.size(PartejarTheme.dimensions.iconLarge * 2),
                        shape = PartejarShapes.radius28,
                        color = PartejarTheme.colors.successContainer
                    ) {
                        Box(contentAlignment = Alignment.Center) {
                            Icon(
                                imageVector = PartejarIcons.Feedback.success,
                                contentDescription = null,
                                modifier = Modifier.size(PartejarTheme.dimensions.iconLarge),
                                tint = PartejarTheme.colors.success
                            )
                        }
                    }
                    Column(verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
                        Text(
                            text = "Tudo em dia por aqui",
                            modifier = Modifier.semantics { heading() },
                            style = MaterialTheme.typography.headlineSmall
                        )
                        Text(
                            text = "Não há pendências, próximas gestantes ou compromissos para mostrar.",
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                }
                Column(
                    modifier = Modifier.weight(0.85f),
                    verticalArrangement = Arrangement.spacedBy(spacing.space12)
                ) {
                    Text("Visão do dia", style = MaterialTheme.typography.titleMedium)
                    HomeEmptyFact(
                        icon = PartejarIcons.Feedback.success,
                        label = "Pontos de atenção",
                        value = "Nenhum"
                    )
                    HomeEmptyFact(
                        icon = PartejarIcons.Clinical.schedule,
                        label = "Agenda de hoje",
                        value = "Livre"
                    )
                    HomeEmptyFact(
                        icon = PartejarIcons.Navigation.gestantes,
                        label = "Próximas gestantes",
                        value = "Nenhuma"
                    )
                }
            }
        }
    }
}

@Composable
private fun HomeEmptyFact(
    icon: ImageVector,
    label: String,
    value: String
) {
    val spacing = PartejarTheme.spacing
    Row(
        horizontalArrangement = Arrangement.spacedBy(spacing.space12),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(
            imageVector = icon,
            contentDescription = null,
            modifier = Modifier.size(PartejarTheme.dimensions.iconSmall),
            tint = MaterialTheme.colorScheme.primary
        )
        Column(verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
            Text(
                text = label,
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            Text(text = value, style = MaterialTheme.typography.bodyMedium)
        }
    }
}

@Composable
private fun ColumnScope.ExpandedErrorHome(
    message: String,
    dockClearance: Dp,
    onRetry: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .weight(1f)
            .verticalScroll(rememberScrollState())
            .padding(
                start = spacing.space32,
                end = spacing.space32,
                top = spacing.space24,
                bottom = dockClearance
            )
    ) {
        PartejarAlert(
            title = "Algo não saiu como esperado",
            tone = PartejarAlertTone.ERROR,
            modifier = Modifier
                .widthIn(max = 840.dp)
                .fillMaxWidth()
                .align(Alignment.CenterHorizontally),
            action = {
                PartejarButton(
                    text = "Tentar novamente",
                    onClick = onRetry,
                    modifier = Modifier.heightIn(
                        min = PartejarTheme.dimensions.minimumTouchTarget
                    )
                )
            }
        ) {
            Text(message, style = MaterialTheme.typography.bodyMedium)
        }
    }
}

@Composable
private fun AttentionSection(
    items: List<HomeAttentionItemUi>,
    onClick: (() -> Unit)?
) {
    val totalAttentionPoints = items.sumOf { it.quantity }
    val title = if (totalAttentionPoints == 1) {
        "1 ponto precisa da sua atenção"
    } else {
        "$totalAttentionPoints pontos precisam da sua atenção"
    }

    val tone = when {
        items.any { it.level == HomeAttentionLevel.ERROR } -> PartejarAlertTone.ERROR
        items.any { it.level == HomeAttentionLevel.ATTENTION } -> PartejarAlertTone.ATTENTION
        else -> PartejarAlertTone.INFO
    }

    PartejarAlert(
        title = title,
        tone = tone,
        action = {
            if (onClick != null) {
                TextButton(
                    onClick = onClick,
                    modifier = Modifier.heightIn(
                        min = PartejarTheme.dimensions.minimumTouchTarget
                    )
                ) {
                    Text("Ver alertas", style = MaterialTheme.typography.labelMedium)
                }
            }
        }
    ) {
        Text(
            text = items.joinToString(separator = ", ") { it.summary },
            style = MaterialTheme.typography.bodySmall
        )
    }
}

private fun HomeAttentionItemUi.isCommercialAttention(): Boolean =
    type == HomePendingType.FOLLOW_UP

private fun HomeUiState.Content.dailyBriefing(): String {
    val entries = buildList {
        todayAppointments.size.takeIf { it > 0 }?.let { count ->
            add(if (count == 1) "1 compromisso hoje" else "$count compromissos hoje")
        }
        attentionItems.sumOf { it.quantity }.takeIf { it > 0 }?.let { count ->
            add(if (count == 1) "1 ponto de atenção" else "$count pontos de atenção")
        }
    }
    return entries.joinToString(" · ").ifEmpty { "Seu dia está organizado." }
}

@Composable
private fun SectionHeader(
    icon: ImageVector,
    title: String,
    subtitle: String? = null,
    action: (@Composable () -> Unit)? = null
) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(spacing.space8),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(
            imageVector = icon,
            contentDescription = null,
            modifier = Modifier.size(dimensions.iconDefault),
            tint = MaterialTheme.colorScheme.primary
        )
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = title,
                modifier = Modifier.semantics { heading() },
                style = MaterialTheme.typography.titleLarge
            )
            subtitle?.let {
                Text(
                    text = it,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
        action?.invoke()
    }
}

@Composable
private fun ColumnScope.LoadingHome(dockClearance: Dp, horizontalGutter: Dp) {
    val spacing = PartejarTheme.spacing
    LazyColumn(
        modifier = Modifier
            .fillMaxWidth()
            .weight(1f),
        contentPadding = PaddingValues(
            start = horizontalGutter,
            end = horizontalGutter,
            top = spacing.space4,
            bottom = dockClearance
        ),
        verticalArrangement = Arrangement.spacedBy(spacing.space16)
    ) {
        item {
            PartejarCard(Modifier.fillMaxWidth()) {
                Column(verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
                    PartejarSkeletonText(Modifier.fillMaxWidth(0.46f).height(spacing.space20))
                    PartejarSkeletonText(Modifier.fillMaxWidth().height(spacing.space12))
                    PartejarSkeletonText(Modifier.fillMaxWidth(0.72f).height(spacing.space12))
                }
            }
        }
        item {
            PartejarSkeletonText(Modifier.fillMaxWidth(0.58f).height(spacing.space24))
        }
        items(2) {
            PartejarCard(Modifier.fillMaxWidth()) {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(spacing.space12),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    PartejarSkeleton(Modifier.size(PartejarTheme.dimensions.minimumTouchTarget))
                    Column(
                        modifier = Modifier.weight(1f),
                        verticalArrangement = Arrangement.spacedBy(spacing.space8)
                    ) {
                        PartejarSkeletonText(Modifier.fillMaxWidth(0.7f).height(spacing.space16))
                        PartejarSkeletonText(Modifier.fillMaxWidth(0.5f).height(spacing.space12))
                    }
                }
            }
        }
    }
}

@Composable
private fun ColumnScope.EmptyHome(dockClearance: Dp) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .weight(1f)
            .padding(bottom = dockClearance)
            .padding(spacing.space24),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        Icon(
            imageVector = PartejarIcons.Feedback.success,
            contentDescription = null,
            modifier = Modifier.size(dimensions.iconLarge),
            tint = PartejarTheme.colors.success
        )
        Text(
            text = "Tudo em dia por aqui",
            modifier = Modifier
                .padding(top = spacing.space12)
                .semantics { heading() },
            style = MaterialTheme.typography.titleLarge
        )
        Text(
            text = "Não há pendências, próximas gestantes ou compromissos para mostrar.",
            modifier = Modifier.padding(top = spacing.space8),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
    }
}

@Composable
private fun ColumnScope.ErrorHome(
    message: String,
    dockClearance: Dp,
    horizontalGutter: Dp,
    onRetry: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .weight(1f)
            .padding(bottom = dockClearance)
            .padding(
                start = horizontalGutter,
                end = horizontalGutter,
                top = spacing.space16,
                bottom = spacing.space16
            ),
        verticalArrangement = Arrangement.Center
    ) {
        PartejarAlert(
            title = "Algo não saiu como esperado",
            tone = PartejarAlertTone.ERROR
        ) {
            Text(message, style = MaterialTheme.typography.bodyMedium)
        }
        PartejarButton(
            text = "Tentar novamente",
            onClick = onRetry,
            modifier = Modifier.padding(top = spacing.space16)
        )
    }
}

private fun HomePregnancyUi.toSummary() = GestanteSummary(
    name = name,
    babyName = babyName,
    gestationalWeeks = gestationalWeeks,
    gestationalDays = gestationalDays,
    dueDate = dueDate,
    responsible = responsible,
    status = actionRequired?.let(GestanteSummaryStatus::ActionRequired)
        ?: GestanteSummaryStatus.OnTrack
)
